import { useState, useMemo } from "react";
import dayjs from "dayjs";
import {
  useGetTopInventoryOutReport,
  useDownloadTopInventoryOutReportExcel
} from "@app/services/topInventoryOutReport/hooks";
import { useGetSiteList, useGetUsersByModule } from "@app/services/common/hooks";
import { GCidAccount } from "@app/config/globalsConst";
import { warningNotification } from "@app/common/components/notifications";
import { downloadExcel } from "@app/utils/downloadExcel";

import ExcelIcon from "@app/assets/excel.svg";

import { ActionIcon, Grid, Image, MultiSelect, Select, Table, TextInput } from "@mantine/core";
import { Template } from "@app/common/components/Template";
import { FilterSearchButton } from "@app/common/components/SearchButtonFilters";
import AppPagination from "@app/common/components/AppPagination";

import type {
  ITopInventoryOutReportFilters,
  ITopInventoryOutReportItem
} from "@app/services/topInventoryOutReport/types";

const defaultFilters: ITopInventoryOutReportFilters = {
  startDate: dayjs().format("YYYY-MM-DD"),
  endDate: dayjs().format("YYYY-MM-DD"),
  siteIds: "",
  warehouseName: "",
  deliveredBy: null,
  page: 1,
  perpage: 10
};

const TopInventoryOutReport = () => {
  const [filters, setFilters] = useState<ITopInventoryOutReportFilters>(defaultFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<ITopInventoryOutReportFilters>(defaultFilters);
  const [hasSubmittedFilters, setHasSubmittedFilters] = useState(true);

  const { data: reportData } = useGetTopInventoryOutReport(appliedFilters, hasSubmittedFilters);
  const { refetch: downloadExcel_ } = useDownloadTopInventoryOutReportExcel(appliedFilters);
  const { data: sitesData } = useGetSiteList({ eaccount: GCidAccount() });
  const { data: usersData } = useGetUsersByModule();

  const siteOptions = useMemo(() => {
    if (!sitesData?.results) {
      return [];
    }
    return sitesData.results.map((site) => ({
      value: String(site.siteId),
      label: site.site
    }));
  }, [sitesData?.results]);

  const usersList = useMemo(() => {
    if (!usersData?.results) {
      return [];
    }
    return usersData.results.map((user) => ({
      value: String(user.id),
      label: `${user.first_name} ${user.first_surname}${user.second_surname ? ` ${user.second_surname}` : ""}`
    }));
  }, [usersData?.results]);

  const handleSubmitFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!filters.startDate || !filters.endDate) {
      warningNotification("Los campos 'Fecha desde' y 'Fecha hasta' son obligatorios");
      return;
    }

    const nextFilters = { ...filters, page: 1 };
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setHasSubmittedFilters(true);
  };

  const handleChangePage = (value: number) => {
    setFilters((state) => ({ ...state, page: value }));

    if (hasSubmittedFilters) {
      setAppliedFilters((state) => ({ ...state, page: value }));
    }
  };

  const handleDownloadExcel = async () => {
    const result = await downloadExcel_();
    if (result.data?.results?.base64) {
      downloadExcel(result.data.results.base64, "Informe salida para consumo inventario TOP.xlsx");
    }
  };

  const filtersHeader = () => {
    return (
      <Grid columns={13} align="flex-end" mb="lg" w="100%">
        <Grid.Col span={2}>
          <TextInput
            data-testid="topinventoryreport-start-date-input"
            label="Fecha desde"
            type="date"
            max={filters.endDate || undefined}
            placeholder="Escribir..."
            value={filters.startDate}
            onChange={({ target }) => {
              setFilters((state) => ({ ...state, startDate: target.value }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <TextInput
            data-testid="topinventoryreport-end-date-input"
            label="Fecha hasta"
            type="date"
            min={filters.startDate || undefined}
            placeholder="Escribir..."
            value={filters.endDate}
            onChange={({ target }) => {
              setFilters((state) => ({ ...state, endDate: target.value }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <MultiSelect
            data-testid="topinventoryreport-sites-multiselect"
            label="Sedes"
            data={siteOptions}
            searchable
            clearable
            placeholder="Seleccionar..."
            value={filters.siteIds ? filters.siteIds.split(",") : []}
            onChange={(values) => {
              setFilters((state) => ({ ...state, siteIds: values.join(","), page: 1 }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <TextInput
            data-testid="topinventoryreport-warehouse-input"
            label="Almacén"
            placeholder="Nombre del almacén..."
            value={filters.warehouseName}
            onChange={({ target }) => {
              setFilters((state) => ({ ...state, warehouseName: target.value, page: 1 }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <Select
            data-testid="topinventoryreport-user-select"
            label="Usuario"
            data={usersList}
            limit={15}
            clearable
            searchable
            placeholder="Seleccionar..."
            value={filters.deliveredBy}
            onChange={(value) => {
              setFilters((state) => ({ ...state, deliveredBy: value, page: 1 }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <FilterSearchButton />
        </Grid.Col>
        <Grid.Col span="auto" className="flex justify-end">
          <ActionIcon
            variant="subtle"
            onClick={handleDownloadExcel}
            data-testid="topinventoryreport-download-button"
          >
            <Image src={ExcelIcon} />
          </ActionIcon>
        </Grid.Col>
      </Grid>
    );
  };

  const renderRow = (option: ITopInventoryOutReportItem, index: number) => {
    return (
      <Table.Tr key={index} data-testid={`topinventoryreport-row-${index}`}>
        <Table.Td>{option?.sede}</Table.Td>
        <Table.Td ta="center">{option?.topCode}</Table.Td>
        <Table.Td>{option?.articleName}</Table.Td>
        <Table.Td ta="center">{option?.lot}</Table.Td>
        <Table.Td>{option?.topWarehouse}</Table.Td>
        <Table.Td>{option?.dispensingUser}</Table.Td>
        <Table.Td ta="center">{option?.quantity}</Table.Td>
      </Table.Tr>
    );
  };

  const renderTable = () => {
    return (
      <Table className="biowel-table" data-testid="topinventoryreport-table">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Sede</Table.Th>
            <Table.Th ta="center">Código TOP</Table.Th>
            <Table.Th>Nombre del artículo</Table.Th>
            <Table.Th ta="center">Lote</Table.Th>
            <Table.Th>Almacén TOP</Table.Th>
            <Table.Th>Usuario que dispensó</Table.Th>
            <Table.Th ta="center">Cantidad</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {reportData?.results?.length ? (
            reportData.results.map((item, index) => renderRow(item, index))
          ) : (
            <Table.Tr>
              <Table.Td ta="center" colSpan={7} data-testid="topinventoryreport-no-data">
                No hay información para mostrar.
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    );
  };

  return (
    <Template title="Informe salida para consumo inventario TOP">
      <form onSubmit={handleSubmitFilters} data-testid="topinventoryreport-filters">
        {filtersHeader()}
      </form>

      {renderTable()}

      <AppPagination
        total={reportData?.rowTotal || 0}
        withDetails
        value={filters.page || 1}
        onChange={handleChangePage}
        data-testid="topinventoryreport-pagination"
      />
    </Template>
  );
};

export default TopInventoryOutReport;
